import { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import type { Node } from '@/types/node';
import type { ReloadNginxResponse } from '@/types/nginx';

export interface ReloadNginxModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Nodes that can be reloaded (online) */
  nodes: Node[];
  onReload: (nodeId: string) => Promise<ReloadNginxResponse>;
}

/**
 * Zero-downtime nginx reload on a node; shows the result in place.
 * Mount only while open (or with a `key`) to reset the form.
 */
export function ReloadNginxModal({ isOpen, onClose, nodes, onReload }: ReloadNginxModalProps) {
  const [nodeId, setNodeId] = useState('');
  const [result, setResult] = useState<{ status: string; message: string } | null>(null);
  const reload = useAsyncAction(onReload, { errorMessage: 'Failed to reload nginx' });
  const isReloading = reload.isPending();

  const handleReload = async () => {
    if (!nodeId) return;
    setResult(null);
    const outcome = await reload.run(nodeId);
    setResult(outcome.ok ? outcome.value : { status: 'failed', message: outcome.error });
  };

  const succeeded = result?.status === 'success';

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalHeader>Reload NGINX</ModalHeader>
      <ModalBody>
        <p className="nginx-modal-description">
          Select a node to reload NGINX. This performs a zero-downtime reload
          to apply the latest configuration changes.
        </p>

        <div className="nginx-modal-field">
          <label htmlFor="reload-node-select" className="nginx-modal-label">
            Node *
          </label>
          <Select
            id="reload-node-select"
            value={nodeId}
            onChange={setNodeId}
            placeholder="Select a node..."
            options={nodes.map((node) => ({ value: node.id, label: `${node.name} (${node.ip_address})` }))}
            className="nginx-modal-select"
            disabled={isReloading}
            required
          />
          {nodes.length === 0 && (
            <p className="nginx-modal-hint nginx-modal-hint-warning">
              No online nodes available. Nodes must be online to reload NGINX.
            </p>
          )}
        </div>

        {result && (
          <div
            className={`nginx-reload-result ${succeeded ? 'nginx-reload-result-success' : 'nginx-reload-result-failed'}`}
            role="status"
          >
            <span className="nginx-reload-result-status">{succeeded ? 'Success' : 'Failed'}</span>
            <span className="nginx-reload-result-message">{result.message}</span>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose} disabled={isReloading}>
          {result ? 'Close' : 'Cancel'}
        </Button>
        {!result && (
          <Button onClick={handleReload} isLoading={isReloading} disabled={!nodeId || isReloading}>
            Reload
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
}
